export interface BrgListItemDto {
  brgId: number;
  name: string;
  status: 'Active' | 'Inactive' | 'Pending' | 'Deleted';
  memberCount: number;
  budget?: number;
  spend?: number;
  colorCode?: string;
}

export interface BrgColorKeyDto {
  brgId: number;
  name: string;
  colorCode?: string;
}

export interface BrgDetailDto {
  brgId: string;
  name: string;
  description: string;
  status: string;
  establishedDate?: string;
  fiscalYearBudget?: number;
  colorCode?: string;
  isAdminOnly: boolean;
  roleAssignments?: RoleAssignmentDto[];
  totalMembers: number;
  activeMembers: number;
  totalEventsYtd: number;
  budgetUtilizationPct?: number;
  createdAt: string;
  updatedAt: string;
}

export interface RoleAssignmentDto {
  roleId: number;
  roleCode: string;
  roleDesc?: string;
  userId?: number;
  userName?: string;
}

export interface RoleDefinitionDto {
  roleId: number;
  roleCode: string;
  roleDesc?: string;
  isRequired: boolean;
}

export interface EngagementSummaryDto {
  totalEngaged: number;
  in1Brg: number;
  in2Brgs: number;
  in3Brgs: number;
  in4Brgs: number;
  in5OrMoreBrgs: number;
}

export interface RoleAssignmentRequest {
  roleId: number;
  userId?: number;
}

export interface CreateBrgRequest {
  name: string;
  description: string;
  roleAssignments: RoleAssignmentRequest[];
}

export interface UpdateBrgRequest extends CreateBrgRequest {}

export interface MemberBrgMembership {
  brgId: string;
  brgName: string;
  role: string;
  joinDate?: string;
}

export interface MemberListItemDto {
  userId: string;
  azureAdUserId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  isActive: boolean;
  role: string;
  brgMemberships: MemberBrgMembership[];
}

export interface MemberDetailDto {
  userId: string;
  azureAdUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  companyName?: string;
  department?: string;
  jobTitle?: string;
  isActive: boolean;
  brgMemberships: MemberBrgMembership[];
}

export interface UserDropdownDto {
  userId: number;
  displayName: string;
  email: string;
}

export interface BudgetRequestListItemDto {
  budgetRequestId: number;
  brgId: number;
  fiscalYear?: string;
  projectEvent?: string;
  purposeDescription?: string;
  requestAmount: number;
  budgetStatusId: number;
  approvedAmount: number;
  requestedDate: string;
  requestedById?: number;
  scheduledDate?: string;
  requestDescription?: string;
  spentAmount: number;
  mmRecognitionId?: number;
}

export interface BudgetRequestDetailDto {
  budgetRequestId: number;
  brgId: number;
  title: string;
  description?: string;
  amount: number;
  budgetStatusId: number;
  approvalStatus: string;
  approvalNotes?: string;
  approvedById?: number;
  approvedAt?: string;
  fiscalYear?: string;
  quarter?: string;
  pillar?: string;
  vendorName?: string;
  purchaseOrderNumber?: string;
  actualSpend?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBudgetRequestRequest {
  brgId: number;
  fiscalYear?: string;
  projectEvent?: string;
  purposeDescription?: string;
  requestDescription?: string;
  requestAmount?: number;
  scheduledDate?: string;
  budgetStatusId?: number;
  mmRecognitionId?: number;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export interface LeadRoleDto {
  roleName: string;
  count: number;
}

export interface BrgMembershipChartDto {
  brgId: number;
  brgName: string;
  memberCount: number;
}

export interface CompanyDto {
  companyId: number;
  companyCode: string;
  companyName: string;
}

export interface MembershipSummaryDto {
  totalEngagedEmployees: number;
  in1Brg: number;
  in2Brgs: number;
  in3Brgs: number;
  in4Brgs: number;
  in5OrMoreBrgs: number;
  leadRoles: LeadRoleDto[];
  brgMemberships: BrgMembershipChartDto[];
  companies: CompanyDto[];
}

// Project Tracking Types
export interface ProjectTaskDto {
  taskId: number;
  projectId: number;
  taskName: string;
  ownerUserId?: number;
  ownerName?: string;
  startDate?: string;
  dueDate?: string;
  status: string;
  priority: string;
  notes?: string;
}

export interface ProjectMessageDto {
  messageId: number;
  projectId: number;
  parentMessageId?: number;
  createdByUserId: number;
  createdByName: string;
  messageContent: string;
  createdAt: string;
  replies: ProjectMessageDto[];
}

export interface ProjectDto {
  projectId: number;
  projectName: string;
  brgId: number;
  brgName: string;
  location?: string;
  eventDate: string;
  startTime?: string;
  endTime?: string;
  budget?: number;
  statusId?: number;
  statusName?: string;
  projectDescription?: string;
  goalsAlignment?: string;
  notes?: string;
  createdByName: string;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
  tasks: ProjectTaskDto[];
  messages: ProjectMessageDto[];
}

export interface CreateProjectRequest {
  brgId: number;
  projectName: string;
  location?: string;
  eventDate: string;
  startTime?: string;
  endTime?: string;
  budget?: number;
  statusId?: number;
  projectDescription?: string;
  goalsAlignment?: string;
  notes?: string;
}

export interface UpdateProjectRequest {
  projectName: string;
  location?: string;
  eventDate: string;
  startTime?: string;
  endTime?: string;
  budget?: number;
  statusId?: number;
  projectDescription?: string;
  goalsAlignment?: string;
  notes?: string;
}

export interface CreateProjectTaskRequest {
  taskName: string;
  ownerUserId?: number;
  startDate?: string;
  dueDate?: string;
  status: string;
  priority: string;
  notes?: string;
}

export interface UpdateProjectTaskRequest {
  taskName: string;
  ownerUserId?: number;
  startDate?: string;
  dueDate?: string;
  status: string;
  priority: string;
  notes?: string;
}

export interface CreateProjectMessageRequest {
  projectId: number;
  parentMessageId?: number;
  messageContent: string;
}

// ─── OKR's and BRG Smart Goals Types ───────────────────────────────────────

export type GoalPillar = 'Culture' | 'Community' | 'Career';
export type GoalStatus = 'Accomplished' | 'In Progress' | 'Not Going to Meet It';

export interface GoalStatusAudit {
  previousStatus?: GoalStatus;
  newStatus: GoalStatus;
  updatedByName: string;
  updatedAt: string;
}

export interface BrgGoalDto {
  goalId: string;
  brgId: string;
  brgName: string;
  goalText: string;
  keyResultText: string;
  pillar: GoalPillar;
  fiscalYear: string;
  status: GoalStatus;
  createdAt: string;
  createdByName: string;
  updatedAt?: string;
  statusAuditTrail: GoalStatusAudit[];
}

export interface CreateGoalRequest {
  brgId: string;
  goalText: string;
  keyResultText: string;
  pillar: GoalPillar;
  fiscalYear: string;
}

export interface UpdateGoalRequest {
  goalText: string;
  keyResultText: string;
  pillar: GoalPillar;
  fiscalYear: string;
  status: GoalStatus;
}

export interface UpdateGoalStatusRequest {
  status: GoalStatus;
}

export interface GoalsFilterParams {
  brgId?: string;
  fiscalYear?: string;
  pillar?: string;
}
